import React, { useMemo, useRef } from 'react'
import {
    Box,
    Center,
    Flex,
    Icon,
    IconButton,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react'
import { LuChevronUp, LuGrip, LuX } from 'react-icons/lu'
import { FreeFocusInside } from 'react-focus-lock'

import { useGetChatQuery } from '~graphql/generated/graphql'
import ChatView from '~components/chats/chat/chat-view'
import { ChatProvider } from '~components/chats/chat/chat-context'
import { useOpenChats } from './open-chats-provider'
import { getClientFullName } from '~utils/helpers'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'
import { ASK_CHCKVET_WIDTH_CSS_VAR } from '~components/ask-chckvet/ask-chckvet-context'

const POPOVER_WIDTH = 380
const POPOVER_HEIGHT = 540
const MIN_POPOVER_WIDTH = 320
const MAX_POPOVER_WIDTH = 720
const MIN_POPOVER_HEIGHT = 360
const MAX_POPOVER_HEIGHT = 840
const MINIMIZED_HEIGHT = 44
const RIGHT_OFFSET = 16
const STACK_GAP = 12

type ChatPopoverSize = {
    width: number
    height: number
}

interface ChatPopoverProps {
    chatId: string
    /** Index in the open-chats stack. 0 = rightmost (most recent). */
    stackIndex: number
}

/**
 * Renders a single fixed-position chat popover anchored to the bottom-right
 * of the viewport. Loads the full Chat object via useGetChatQuery and reuses
 * the existing inbox `ChatView` inside a constrained container. Supports a
 * collapsed/minimized state (just the title bar visible) like FB/LinkedIn.
 */
export default function ChatPopover({ chatId, stackIndex }: ChatPopoverProps) {
    const { closeChat, toggleMinimize, isMinimized, consumeChatOpts } =
        useOpenChats()
    const minimized = isMinimized(chatId)
    const [popoverSize, setPopoverSize] = useLocalStorage<ChatPopoverSize>(
        LocalStorageKeys.chatPopoverSize,
        {
            width: POPOVER_WIDTH,
            height: POPOVER_HEIGHT,
        },
    )
    const resizeStartRef = useRef<{
        pointerX: number
        pointerY: number
        width: number
        height: number
    }>()

    const { data, loading } = useGetChatQuery({
        variables: { id: chatId },
        skip: !chatId,
    })

    const chat = data?.chat

    /**
     * Pull initial opts (aiPrompt, defaultText, patientId) once per popover
     * mount. Consuming clears them from the provider so re-renders won't
     * re-apply, and re-opening the chat later starts fresh.
     */
    const opts = useMemo(() => consumeChatOpts(chatId), [chatId])

    const width = clamp(popoverSize.width, MIN_POPOVER_WIDTH, MAX_POPOVER_WIDTH)
    const expandedHeight = clamp(
        popoverSize.height,
        MIN_POPOVER_HEIGHT,
        MAX_POPOVER_HEIGHT,
    )
    // Shift out from under the Ask Chckvet panel when it's open. The var is
    // published by the dock provider and is 0px whenever the panel is closed.
    const right = `calc(${
        RIGHT_OFFSET + stackIndex * (width + STACK_GAP)
    }px + var(${ASK_CHCKVET_WIDTH_CSS_VAR}, 0px))`
    const height = minimized ? MINIMIZED_HEIGHT : expandedHeight

    function handleResizeStart(event: React.PointerEvent<HTMLDivElement>) {
        if (minimized) return

        event.preventDefault()
        event.stopPropagation()

        const start = {
            pointerX: event.clientX,
            pointerY: event.clientY,
            width,
            height: expandedHeight,
        }
        resizeStartRef.current = start
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    function handleResizeMove(event: React.PointerEvent<HTMLDivElement>) {
        const start = resizeStartRef.current
        if (!start) return

        event.preventDefault()
        event.stopPropagation()

        const nextWidth = clamp(
            start.width + start.pointerX - event.clientX,
            MIN_POPOVER_WIDTH,
            MAX_POPOVER_WIDTH,
        )
        const nextHeight = clamp(
            start.height + start.pointerY - event.clientY,
            MIN_POPOVER_HEIGHT,
            MAX_POPOVER_HEIGHT,
        )

        setPopoverSize({ width: nextWidth, height: nextHeight })
    }

    function handleResizeEnd(event: React.PointerEvent<HTMLDivElement>) {
        if (!resizeStartRef.current) return

        resizeStartRef.current = undefined
        event.currentTarget.releasePointerCapture(event.pointerId)
    }

    return (
        /**
         * `FreeFocusInside` opts this subtree out of any parent focus-locks
         * (Chakra Modal/Drawer use `react-focus-lock` to trap focus inside
         * themselves). Without this, clicking the chat input while a Drawer
         * is open would yank focus right back into the Drawer.
         */
        <Portal>
        <FreeFocusInside>
            <Box
                position="fixed"
                right={right}
                bottom="0"
                w={`${width}px`}
                h={`${height}px`}
                // Portaled to document.body so this beats Chakra Drawer
                // stacking (modal = 1400). Stay below toasts (1700) and
                // tooltips (1800).
                zIndex={1500}
                bg="white"
                borderTopRadius="lg"
                shadow="2xl"
                borderWidth={1}
                borderBottomWidth={0}
                borderColor="gray.200"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                transition={
                    resizeStartRef.current
                        ? undefined
                        : 'height 0.15s ease-out, width 0.15s ease-out'
                }
            >
                {!minimized && (
                    <ResizeHandle
                        onPointerDown={handleResizeStart}
                        onPointerMove={handleResizeMove}
                        onPointerUp={handleResizeEnd}
                        onPointerCancel={handleResizeEnd}
                    />
                )}
                {minimized && chat ? (
                    <MinimizedHeader
                        title={getClientFullName(chat.client) || 'Chat'}
                        onExpand={() => toggleMinimize(chatId)}
                        onClose={() => closeChat(chatId)}
                    />
                ) : loading || !chat ? (
                    <Center flex={1}>
                        <Spinner color="brand.500" />
                    </Center>
                ) : (
                    <ChatProvider
                        chat={chat}
                        initialPatientFilter={
                            opts?.patientId ? [opts.patientId] : undefined
                        }
                    >
                        <ChatView
                            chat={chat}
                            onBack={() => closeChat(chatId)}
                            onCloseWindow={() => closeChat(chatId)}
                            onMinimize={() => toggleMinimize(chatId)}
                            hideClientInfo
                            compact
                            initialDraft={opts?.defaultText}
                            aiPrompt={opts?.aiPrompt}
                            containerProps={{
                                w: 'full',
                                minW: 0,
                                h: 'full',
                                flex: 1,
                                rounded: 0,
                                borderWidth: 0,
                                shadow: 'none',
                            }}
                        />
                    </ChatProvider>
                )}
            </Box>
        </FreeFocusInside>
        </Portal>
    )
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
}

function ResizeHandle({
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
}: {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void
}) {
    return (
        <Center
            position="absolute"
            top={1}
            left={1}
            zIndex={2}
            boxSize={6}
            color="gray.400"
            cursor="nwse-resize"
            rounded="md"
            _hover={{
                color: 'gray.600',
                bg: 'gray.50',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            aria-label="Resize chat"
            role="button"
            title="Drag to resize chat"
        >
            <Icon as={LuGrip} boxSize={4} transform="rotate(45deg)" />
        </Center>
    )
}

interface MinimizedHeaderProps {
    title: string
    onExpand: () => void
    onClose: () => void
}

function MinimizedHeader({ title, onExpand, onClose }: MinimizedHeaderProps) {
    return (
        <Flex
            px={3}
            h="full"
            align="center"
            gap={2}
            bg="white"
            cursor="pointer"
            onClick={onExpand}
            _hover={{ bg: 'gray.50' }}
        >
            <Text
                flex={1}
                minW={0}
                fontWeight="semibold"
                fontSize="sm"
                noOfLines={1}
                color="navy.700"
            >
                {title}
            </Text>
            <IconButton
                aria-label="Expand chat"
                icon={<Icon as={LuChevronUp} fontSize="md" />}
                size="xs"
                variant="ghost"
                rounded="full"
                onClick={e => {
                    e.stopPropagation()
                    onExpand()
                }}
            />
            <IconButton
                aria-label="Close chat"
                icon={<Icon as={LuX} fontSize="md" />}
                size="xs"
                variant="ghost"
                rounded="full"
                onClick={e => {
                    e.stopPropagation()
                    onClose()
                }}
            />
        </Flex>
    )
}
